import { getUser, setUserAvatar, setUserProfile, useUpdateUser } from '@/api/user.api'
import { queryClient } from '@/lib/queryClient'
import { queryKeys } from '@/lib/queryKeys'
import { ref } from 'vue'
import useImpersonation from './useImpersonation'

const user = ref(null as any)
const userStats = ref(null as any)

// Staff impersonation (Lightbase support tab) can optionally land in the product
// dashboard even when the target user has not finished onboarding. Override is
// client-only — the DB `onboarded` flag is left unchanged.
function withImpersonationOverrides(u: any) {
    if (!u) return u
    const { isImpersonating, impersonation } = useImpersonation()
    if (isImpersonating() && impersonation.value?.forceOnboarded !== false) {
        return { ...u, onboarded: true }
    }
    return u
}

export default function useUser() {
    // Seed user state from an aggregate payload (e.g. /dashboard/shell) without
    // an extra /user request. Keeps the TanStack cache in sync so later
    // useGetUser() calls hit the cache.
    const setUser = (u: any) => {
        if (!u) return
        const next = withImpersonationOverrides(u)
        user.value = next
        queryClient.setQueryData(queryKeys.user(), next)
    }

    const setUserStats = (s: any) => {
        userStats.value = s
    }

    const useGetUser = async (options?: { force?: boolean }) => {
        if (options?.force) {
            await queryClient.invalidateQueries({ queryKey: queryKeys.user() })
        }
        const userResp = await queryClient.fetchQuery({
            queryKey: queryKeys.user(),
            queryFn: getUser,
            staleTime: 5 * 60 * 1000,
        })
        const next = withImpersonationOverrides(userResp)
        user.value = next
        return next
    }

    const refreshUser = () => useGetUser({ force: true })

    const updateUser = async (changes: any) => {
        const userResp = await useUpdateUser(changes)
        const next = withImpersonationOverrides(userResp)
        user.value = next
        queryClient.setQueryData(queryKeys.user(), next)
        return next
    }

    const useSetUserAvatar = async (avatar: string) => {
        const userResp = await setUserAvatar(avatar)
        if (!userResp || userResp.error) return user.value
        // The endpoint returns the raw users row (organization as a bare id): keep the expanded user.
        const next = withImpersonationOverrides({ ...user.value, avatar: userResp.avatar })
        user.value = next
        queryClient.setQueryData(queryKeys.user(), next)
        return next
    }

    const updateUserProfile = async (updatedFields: any) => {
        const userResp = await setUserProfile(updatedFields)
        await queryClient.invalidateQueries({ queryKey: queryKeys.user() })
        await useGetUser()
        return userResp
    }

    return {
        user,
        userStats,
        setUser,
        setUserStats,
        useGetUser,
        refreshUser,
        useSetUserAvatar,
        updateUserProfile,
        updateUser
    }
}
